// src/api/protectedApi.js
import axios from 'axios';

const api = axios.create({
  baseURL: 'https://api.binaxpay.online/',
});

/**
 * Clears authentication data and redirects user to sign-in / login page
 */
export const handleUnauthorized = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');

  const currentPath = window.location.pathname;
  if (currentPath !== '/signin' && currentPath !== '/login') {
    window.location.href = '/signin';
  }
};

/**
 * Checks if a response or error indicates an unauthorized / expired session
 */
export const isUnauthorizedResponse = (responseOrError) => {
  if (!responseOrError) return false;

  const status = responseOrError?.status || responseOrError?.response?.status;
  if (status === 401 || status === 403) return true;

  const data = responseOrError?.data || responseOrError?.response?.data;
  const message = data?.message || data?.error || responseOrError?.message || '';

  if (typeof message === 'string') {
    const lower = message.toLowerCase();
    if (
      lower.includes('unauthorized') ||
      lower.includes('invalid token') ||
      lower.includes('token expired') ||
      lower.includes('jwt expired') ||
      lower.includes('session expired') ||
      lower.includes('access denied') ||
      lower.includes('not authenticated')
    ) {
      return true;
    }
  }

  if (data && data.success === false && typeof data.message === 'string' && data.message.toLowerCase().includes('unauthorized')) {
    return true;
  }

  return false;
};

// Request interceptor to attach Bearer / Auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 and { success: false, message: "Unauthorized!" }
api.interceptors.response.use(
  (response) => {
    if (isUnauthorizedResponse(response)) {
      handleUnauthorized();
    }
    return response;
  },
  (error) => {
    if (isUnauthorizedResponse(error)) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export const getData = async (url, params) => {
  try {
    const response = await api.get(url, { params });
    if (isUnauthorizedResponse(response)) {
      handleUnauthorized();
    }
    return response;
  } catch (error) {
    if (isUnauthorizedResponse(error)) {
      handleUnauthorized();
    }
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Something went wrong';

    throw new Error(errorMessage);
  }
};

export const postData = async (url, data) => {
  try {
    const response = await api.post(url, data);
    if (isUnauthorizedResponse(response)) {
      handleUnauthorized();
    }
    return response;
  } catch (error) {
    console.error('❌ Error:', error);

    if (isUnauthorizedResponse(error)) {
      handleUnauthorized();
    }

    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.message ||
      error?.message ||
      'Something went wrong';

    return { data: { success: false, message: errorMessage } };
  }
};

export default api;

